// Families tab — App Flow §5: group list, create group, empty state.
// UI/UX §5.2 — group list on the left, group detail on the right from 768px.
import {
  Button,
  Card,
  Dialog,
  EmptyState,
  Input,
  LoadingState,
  PageHeader,
  SplitView,
  StatusPill,
  cx,
  useToast,
} from '@roots/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { cycleStatusLabel, cycleTone } from '../lib/format.js';
import { FamilyDetailScreen } from './family-detail.js';

export function FamiliesScreen() {
  const { familyId } = useParams({ strict: false }) as { familyId?: string };
  const search = useSearch({ strict: false }) as { join?: string | number };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { push } = useToast();
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(() => Boolean(search.join));
  const [name, setName] = useState('');
  const [link, setLink] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['families'],
    queryFn: () => api.listFamilies(),
  });

  const create = useMutation({
    mutationFn: () =>
      api.createFamily({
        name,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      }),
    onSuccess: (result) => {
      setCreating(false);
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['families'] });
      push(`${result.family.name} is ready — invite your family next.`);
      void navigate({ to: '/families/$familyId', params: { familyId: result.family.id } });
    },
  });

  const list = (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Families"
        action={<Button onClick={() => setCreating(true)}>Create a family</Button>}
      />

      {isLoading && <LoadingState onRetry={() => void refetch()} />}

      {!isLoading && (data?.families.length ?? 0) === 0 && (
        <EmptyState
          message="You’re not in any families yet."
          actions={
            <>
              <Button onClick={() => setCreating(true)}>Create a family</Button>
              <Button variant="secondary" onClick={() => setJoining(true)}>
                I have an invite link
              </Button>
            </>
          }
        />
      )}

      <div className="flex flex-col gap-3">
        {data?.families.map((family) => (
          <Card
            key={family.id}
            className={cx(
              'flex items-center justify-between gap-3',
              family.id === familyId && 'outline outline-2 outline-[var(--color-accent-primary)]',
            )}
          >
            <button
              type="button"
              className="min-w-0 flex-1 text-left"
              aria-current={family.id === familyId ? 'page' : undefined}
              onClick={() =>
                void navigate({ to: '/families/$familyId', params: { familyId: family.id } })
              }
            >
              <p className="type-heading">{family.name}</p>
              <p className="type-caption">
                {family.member_count} members · {family.my_role === 'admin' ? 'Admin' : 'Member'}
              </p>
            </button>
            <StatusPill tone={cycleTone(family.current_cycle_status)}>
              {cycleStatusLabel(family.current_cycle_status)}
            </StatusPill>
          </Card>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <SplitView
        list={list}
        detail={familyId ? <FamilyDetailScreen key={familyId} /> : undefined}
        showDetailOnMobile={Boolean(familyId)}
        emptyDetail={
          (data?.families.length ?? 0) > 0 ? (
            <Card className="py-10 text-center">
              <p className="type-body">Pick a family to see its roster, occasions and pets.</p>
            </Card>
          ) : undefined
        }
      />

      <Dialog
        open={creating}
        onOpenChange={setCreating}
        title="Create a family"
        description="You’ll be the first admin and this month’s editor."
        actions={
          <>
            <Button variant="secondary" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => create.mutate()}
              loading={create.isPending}
              disabled={name.trim().length === 0}
            >
              Create
            </Button>
          </>
        }
      >
        <Input
          label="Family name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="The Johnson Family"
        />
      </Dialog>

      <Dialog
        open={joining}
        onOpenChange={setJoining}
        title="Open your invite link"
        description="Paste the link your family sent you."
        actions={
          <>
            <Button variant="secondary" onClick={() => setJoining(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const token = link.trim().split('/join/').pop();
                if (token) window.location.assign(`/join/${token}`);
              }}
              disabled={!link.includes('/join/')}
            >
              Open
            </Button>
          </>
        }
      >
        <Input
          label="Invite link"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder="https://roots.example.com/join/…"
        />
      </Dialog>
    </>
  );
}
